import React, { useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import kravezLogo from '../Images/KravezLogoHeader.png';
import '../Customize_Css/Login.css';

const ForgotPasswordResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(location.state?.role || 'tenant');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isResetPhase, setIsResetPhase] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email || !role) {
      setError('Email and role are required.');
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/login/api/login/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          role,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('A reset code has been sent to your email.');
        setIsResetPhase(true);
      } else {
        setError(data.message || 'Failed to send reset code.');
      }
    } catch (error) {
      console.error('Error in forgot password:', error);
      setError('An error occurred. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!code || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/login/api/login/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: code.trim(),
          newPassword,
          role,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Password reset successfully. Redirecting to login...');
        setTimeout(() => navigate('/'), 2000);
      } else {
        setError(data.message || 'Failed to reset password.');
      }
    } catch (error) {
      console.error('Error in reset password:', error);
      setError('An error occurred. Please try again.');
    }
  };

  return (
    <main className="login-screen">
      <section className="login-brand">
        <img className="login-kravez-logo" src={kravezLogo} alt="Kravez" />
        <div className="login-brand-copy">
          <h1>{isResetPhase ? 'Set a new password.' : 'Reset your password.'}</h1>
          <p>
            {isResetPhase
              ? 'Use the code from your email, then choose a new password.'
              : 'Enter the email for your tenant or employee account.'}
          </p>
        </div>
        <ul className="login-points">
          <li>
            <span>01</span>
            <div>
              <strong>Email</strong>
              <p>We send a reset code to that address.</p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <strong>Code</strong>
              <p>Enter the code from the message.</p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <strong>Password</strong>
              <p>Choose a new password of at least 8 characters.</p>
            </div>
          </li>
        </ul>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <div className="login-card-head">
            <div>
              <p className="login-kicker">{isResetPhase ? 'Step 2 of 2' : 'Step 1 of 2'}</p>
              <h2>{isResetPhase ? 'Reset password' : 'Forgot password'}</h2>
            </div>
          </div>

          {error && <Alert variant="danger" className="login-error">{error}</Alert>}
          {success && <Alert variant="success" className="login-success">{success}</Alert>}

          <Form onSubmit={isResetPhase ? handleResetPassword : handleForgotPassword}>
            {!isResetPhase && (
              <>
                <Form.Group className="login-field" controlId="email">
                  <Form.Label>Email <span className="login-required">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </Form.Group>
                <Form.Group className="login-field" controlId="role">
                  <Form.Label>Role <span className="login-required">*</span></Form.Label>
                  <div className="login-roles" role="radiogroup" aria-label="Role">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={role === 'tenant'}
                      className={role === 'tenant' ? 'is-active' : ''}
                      onClick={() => setRole('tenant')}
                    >
                      Tenant
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={role === 'employee'}
                      className={role === 'employee' ? 'is-active' : ''}
                      onClick={() => setRole('employee')}
                    >
                      Employee
                    </button>
                  </div>
                </Form.Group>
              </>
            )}

            {isResetPhase && (
              <>
                <Form.Group className="login-field" controlId="code">
                  <Form.Label>Reset Code <span className="login-required">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter the reset code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group className="login-field" controlId="newPassword">
                  <Form.Label>New Password <span className="login-required">*</span></Form.Label>
                  <div className="login-password">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-eye"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </Form.Group>
                <Form.Group className="login-field" controlId="confirmPassword">
                  <Form.Label>Confirm Password <span className="login-required">*</span></Form.Label>
                  <div className="login-password">
                    <Form.Control
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-eye"
                      onClick={toggleConfirmPasswordVisibility}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                </Form.Group>
              </>
            )}

            <Button id="login-button" type="submit">
              {isResetPhase ? 'Reset password' : 'Send reset code'}
            </Button>
            <a href="/" className="forgot-password login-back">Back to login</a>
          </Form>
        </div>
      </section>
    </main>
  );
};

export default ForgotPasswordResetPassword;